import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, lastValueFrom, map, merge, take } from 'rxjs';
import { CountryModel } from 'src/app/api/models/country.model';
import { CountryService } from 'src/app/api/services/country.service';
import { DistributorService } from 'src/app/api/services/distributor.service';
import { EligibilityService } from 'src/app/api/services/eligibility.service';
import { AppResource } from 'src/app/app.resource';
import { Esh, NatureResidence, Selection, Selectionne } from 'src/app/core/models/form-state.model';
import { Info } from 'src/app/core/models/info.model';
import { Question } from 'src/app/core/models/question.model';
import { cloneResumeStep } from 'src/app/core/models/resume-step.model';
import { Step } from 'src/app/core/models/step.model';
import { BaseComponent } from 'src/app/shared/components/base-component/base-component.component';
import { MatomoConstants } from 'src/app/shared/constants/matomo.constants';
import { Place } from 'src/app/shared/models/components/place.model';
import { AdresseDataGouvService } from 'src/app/shared/services/adresse-data-gouv.service';
import { Stepper } from '../../../core/models/stepper.model';
import * as fromAddress from '../../../core/state/address';
import * as fromRoot from '../../../core/state/core.state';
import * as fromHousing from '../../../core/state/housing';
import * as fromInfo from '../../../core/state/info';
import * as fromPath from '../../../core/state/path';

import { PathType } from 'src/app/core/enums/path-type.enum';
import {
  HOUSE_APARTMENT_PATH,
  PRIMARY_SECONDARY_PATH,
  ROOT_PATH
} from 'src/app/shared/constants/route.constants';
import { metropolePathSwitch } from 'src/app/shared/helpers/metropole-helper.service';
import { Commune } from 'src/app/shared/models/components/geocodejson.model';

@Component({
  selector: 'app-address',
  templateUrl: './address.component.html',
  styleUrls: ['./address.component.scss']
})
export class AddressComponent extends BaseComponent implements OnInit {
  addressForm!: FormGroup;
  autocompleteAddressForm!: FormGroup;

  isAutocomplete: boolean = true;
  placeSubscription: any;

  cities = new Array<Commune>();
  filteredCity = new Array<Commune>();

  isLoading: boolean = false;
  displayError: boolean = false;
  displayModal: boolean = false;

  errorMessageApi!: string;

  filteredAdresses = new Array<Place>();

  countries = new Array<CountryModel>();
  filteredCountries = new Array<CountryModel>();

  override step: Step = {
    step: this.resource.header.stepHousing,
    stepNumber: 1,
    subStep: this.resource.header.subStepAddress,
    subStepNumber: 1
  };

  override question: Question = {
    libelle: this.resource.question.address,
    popupInfo: undefined
  };

  override info: Info = {
    title: this.resource.infotop.firstStep,
    subTitle: this.resource.infotop.firstStepB,
    logo: this.resource.infotop.logoTime,
    secondTitle: '',
    isVisible: true
  };
  addressType!: Selectionne;

  public override currentPage = MatomoConstants.ADDRESS_ESH;

  path: string = '';

  get address() {
    return this.addressForm.get('address');
  }

  get city() {
    return this.addressForm.get('city');
  }

  get postalCode() {
    return this.addressForm.get('postalCode');
  }

  get country() {
    return this.addressForm.get('country');
  }

  get adresseAutocomplete() {
    return this.autocompleteAddressForm?.get('address')!;
  }

  constructor(
    store: Store<fromRoot.State>,
    resources: AppResource,
    private readonly router: Router,
    private readonly adresseDataGouvService: AdresseDataGouvService,
    private readonly eligibilityService: EligibilityService,
    private readonly countryService: CountryService,
    private readonly distributorService: DistributorService
  ) {
    super(store, resources);

    this.errorMessageApi = this.resource.error.gouvAddressText;
  }

  override async ngOnInit() {
    super.ngOnInit();

    await this.updateStepper(true);
    this.updateResumeStepVisibility(false);
    await this.loadCountries();
    this.initForm();
    this.initData();

    this.path = await lastValueFrom(this.store.select(fromPath.selectPathType).pipe(take(1)));
    console.log('path', this.path);
  }

  async loadCountries() {
    this.countries = await lastValueFrom(this.countryService.GetCountries().pipe(take(1)));
    this.filteredCountries = this.countries;
  }

  private async initData() {
    this.addressType = await lastValueFrom(
      this.store.select(fromAddress.selectAddressTypeData).pipe(take(1))
    );
    if (this.addressType == Selectionne.Undefined) {
      this.store.dispatch(fromAddress.patchSelectedAddress({ adresseType: Selectionne.Selection }));
    } else {
      await this.initAddress();
    }
  }

  private initForm() {
    this.addressForm = new FormGroup({
      country: new FormControl<CountryModel | null>(
        this.countries.find(x => x.countryNameFr == 'France')!,
        {
          validators: [Validators.required],
          nonNullable: true,
          updateOn: 'change'
        }
      ),
      postalCode: new FormControl<string | null>(null, {
        validators: [Validators.required, Validators.pattern('^[0-9]{5}$')],
        nonNullable: true,
        updateOn: 'change'
      }),
      address: new FormControl<string | null>('', {
        validators: [Validators.required],
        nonNullable: true,
        updateOn: 'blur'
      }),
      city: new FormControl<string | null>('', {
        validators: [Validators.required],
        nonNullable: true,
        updateOn: 'blur'
      })
    });

    this.autocompleteAddressForm = new FormGroup({
      address: new FormControl<Place | null>(null, {
        validators: [Validators.required],
        nonNullable: true,
        updateOn: 'change'
      })
    });

    this.postalCode!.valueChanges.subscribe(postalCode => {
      if (postalCode?.length == 5) {
        this.adresseDataGouvService.getCityByPostalCode(postalCode).subscribe({
          next: (res: Commune[]) => {
            if (res) {
              this.cities = res;
              this.city?.setValue(res[0]);
            }
          }
        });
      } else {
        this.city?.setValue('');
      }
    });

    const addressSelection$ = this.adresseAutocomplete?.valueChanges.pipe(
      map((val: any) => {
        let Adresse = '';
        if (val) {
          Adresse = val.place;
          let AdresseEsh = {
            CodePostal: val.postalCode,
            Ville: val.city,
            Numero: val.streetNumber,
            Rue: val.street,
            NumeroEtRue: `${val.streetNumber}, ${val.street}`,
            Pays: val.country
          };
          return { Adresse, AdresseEsh } as Partial<Selection>;
        }
        return { Adresse } as Partial<Selection>;
      })
    );

    const address$ = this.address?.valueChanges.pipe(
      map((NumeroEtRue: string) => ({ NumeroEtRue } as Partial<Esh>))
    );
    const postalCode$ = this.postalCode?.valueChanges.pipe(
      map((CodePostal: string) => ({ CodePostal } as Partial<Esh>))
    );
    const city$ = this.city?.valueChanges.pipe(map((Ville: string) => ({ Ville } as Partial<Esh>)));

    const country$ = this.country?.valueChanges.pipe(
      map((val: CountryModel) => {
        let Pays = val?.countryNameFr;
        return { Pays } as Partial<Esh>;
      })
    );

    merge(address$!, postalCode$!, city$!, country$!).subscribe((payload: Partial<Esh>) => {
      this.store.dispatch(fromAddress.patchManuelle({ payload }));
    });

    addressSelection$?.subscribe((payload: Partial<Selection>) => {
      this.store.dispatch(fromAddress.patchSelection({ payload }));
    });

    // Le parcours ne prend en charge que les adresses en France en saisie manuelle
    this.country?.valueChanges.pipe(distinctUntilChanged()).subscribe((country: CountryModel) => {
      if (!this.isAutocomplete && country && country.countryNameFr !== 'France') {
        this.displayModal = true;
      }
    });

    this.addressForm.valueChanges
      .pipe(
        map(() => this.addressForm.valid),
        distinctUntilChanged()
      )
      .subscribe((isValid: boolean) => {
        this.store.dispatch(fromAddress.changeValidationStatus({ isValid }));
      });

    this.autocompleteAddressForm.valueChanges
      .pipe(
        map(() => this.autocompleteAddressForm.valid),
        distinctUntilChanged()
      )
      .subscribe((isValid: boolean) => {
        this.store.dispatch(fromAddress.changeValidationStatus({ isValid }));
      });
  }

  private async initAddress() {
    if (this.addressType == Selectionne.Manuel) {
      this.isAutocomplete = false;
      let address = await lastValueFrom(
        this.store.select(fromAddress.selectAddressManuelleGroupData).pipe(take(1))
      );
      this.address?.patchValue(address.NumeroEtRue, { emitEvent: false });
      this.postalCode?.patchValue(address.CodePostal, { emitEvent: false });
      this.city?.patchValue(address.Ville, { emitEvent: false });
      this.country?.patchValue(this.countries.find(x => x.countryNameFr == address.Pays)!, {
        emitEvent: false
      });
    } else if (this.addressType == Selectionne.Selection) {
      let selected = await lastValueFrom(
        this.store.select(fromAddress.selectAddressSelectionGroupData).pipe(take(1))
      );
      if (selected) {
        this.adresseDataGouvService.search(selected.Adresse, 1).subscribe({
          next: res => {
            let filtered = res.features.map(feature => {
              const properties = feature.properties;
              return {
                place: `${properties.name}, ${properties.postcode} ${properties.city}, France`,
                id: properties.id,
                postalCode: properties.postcode,
                country: 'FR',
                city: properties.city,
                streetNumber: properties.housenumber,
                street: properties.street
              };
            });
            this.filteredAdresses = filtered;
            let currentAddress = filtered.find(item => selected?.Adresse === item.place);
            if (currentAddress != null && currentAddress.place !== '') {
              this.adresseAutocomplete.patchValue(currentAddress, { emitEvent: false });
            }
          }
        });
      }
    }
  }

  public onNotFoundAddressClick() {
    this.store.dispatch(fromAddress.patchSelectedAddress({ adresseType: Selectionne.Manuel }));
    this.isAutocomplete = false;
    this.errorMessageApi = this.resource.error.addressFreeText;
  }

  public onWantToUseAutoCompleteClick() {
    this.store.dispatch(fromAddress.patchSelectedAddress({ adresseType: Selectionne.Selection }));
    this.isAutocomplete = true;
    this.errorMessageApi = this.resource.error.gouvAddressText;
  }

  async filterAddress($event: any) {
    this.adresseDataGouvService.search($event.query, 1).subscribe({
      next: res => {
        if (res) {
          let filtered = res.features.map(feature => {
            const properties = feature.properties;
            return {
              place: `${properties.name}, ${properties.postcode} ${properties.city}, France`,
              id: properties.id,
              postalCode: properties.postcode,
              country: 'FR',
              city: properties.city,
              streetNumber: properties.housenumber,
              street: properties.street
            };
          });
          this.filteredAdresses = filtered;
        }
      }
    });
  }

  filterCity($event: any) {
    let query = $event.query;
    if (query != null) {
      var trimmedQuery = query
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLocaleLowerCase('fr-FR');
      this.filteredCity = this.cities.filter(item => {
        return (item.nomCommune ?? '')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLocaleLowerCase('fr-FR')
          .includes(trimmedQuery);
      });
    } else {
      this.filteredCity = this.cities;
    }
  }

  async filterCountries($event: any) {
    let query = $event.query;
    if (query != null) {
      let trimmedQuery = query
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLocaleLowerCase('fr-FR');
      this.filteredCountries = this.countries.filter(item => {
        return item.countryNameFr
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLocaleLowerCase('fr-FR')
          .includes(trimmedQuery);
      });
    } else {
      this.filteredCountries = this.countries;
    }
  }

  checkPostalCode() {
    const postalCode = this.isAutocomplete
      ? (this.adresseAutocomplete?.value as Place).postalCode
      : this.postalCode?.value;

    if (this.path !== PathType.HOUSING_ETUDIANTE) metropolePathSwitch(postalCode, this.store);
  }

  async valider() {
    if (
      !this.isAutocomplete &&
      this.country?.value &&
      this.country.value.countryNameFr !== 'France'
    ) {
      this.displayModal = true;
      return;
    }

    // Sauvegarde du stepper
    await this.updateStepper(false);
    // Sauvegarde de l'étape
    await this.updateResumeStep();

    this.checkPostalCode();
    // Redirection vers l'étape suivante

    if (this.path === PathType.HOUSING_ETUDIANTE) {
      this.store.dispatch(
        fromHousing.patchHousingNature({
          payload: { NatureResidence: NatureResidence.ForRent }
        })
      );
      this.router.navigate([`${ROOT_PATH}${HOUSE_APARTMENT_PATH}`]);
    } else {
      this.router.navigate([`${ROOT_PATH}${PRIMARY_SECONDARY_PATH}`]);
    }
  }

  async updateStepper(init: boolean) {
    let stepper = await lastValueFrom(this.store.select(fromInfo.selectStepper).pipe(take(1)));
    let tmpStepper = JSON.parse(JSON.stringify(stepper)) as Stepper;

    let title = tmpStepper.devis.titles.find(
      (x: { libelle: string }) => x.libelle == this.resource.stepper.lodging
    );

    let subtitleS = title?.subtitles.find(
      (x: { url: string }) => x.url == this.resource.stepper.address
    );

    if (init) {
      if (subtitleS) {
        subtitleS.isActive = true;
        subtitleS.isVisible = true;
      }
    } else {
      if (subtitleS) subtitleS.isValid = true;

      let subtitleN = title?.subtitles.find(
        (x: { url: string }) => x.url == this.resource.stepper.houseapartment
      );

      if (subtitleN) subtitleN.isActive = true;
    }

    this.store.dispatch(fromInfo.updateStepper({ payload: tmpStepper }));
  }

  async updateResumeStep() {
    const resumeStep = await lastValueFrom(
      this.store.select(fromInfo.selectCurrentResumeStep).pipe(take(1))
    );
    const tmpResumeStep = cloneResumeStep(resumeStep);

    let stepResumeStep = tmpResumeStep.steps.find(
      (x: { id: string }) => x.id == this.resource.resumestep.address
    );
    let libelle = '';

    // WE ONLY DISPLAY THE NAME OF THE PAGE
    libelle = this.resource.header.subStepAddress;
    // if (this.isAutocomplete) libelle = this.adresseAutocomplete?.value.place;
    // else libelle = this.address?.value + ' ' + this.postalCode?.value + ' ' + this.city?.value;

    if (stepResumeStep) {
      stepResumeStep.step = libelle;
      stepResumeStep.isVisible = true;
    } else {
      tmpResumeStep.steps.push({
        id: this.resource.resumestep.address,
        step: libelle,
        url: this.resource.resumestep.address,
        isVisible: true
      });
    }
    this.store.dispatch(fromInfo.updateResumeStep({ payload: tmpResumeStep }));
  }

  cancel() {
    this.displayModal = false;
  }
}
